import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { FileCheck2, Lock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '../../layouts/PageHeader';
import { EmptyState } from '../Common/EmptyState';
import { useCapabilities } from '../../../context/CapabilityContext';
import PaymentQueueTab from './payments/PaymentQueueTab';
import PaymentFilesTab from './payments/PaymentFilesTab';
import { apiError, fetchFiles, fetchQueue } from './payments/paymentsApi';

/**
 * Betalningar — the home of ISO 20022 payment files: the queue of payments
 * waiting to go into a file, and the files themselves (download, mark
 * uploaded, cancel). Route: /:companyId/payments?tab=queue|files&select=sala&file=<id>
 */
const PaymentsPage = () => {
  const { companyId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const tab = params.get('tab') === 'files' ? 'files' : 'queue';
  const selectedFile = params.get('file');

  // The API is admin-only. Until the capabilities have loaded nothing is
  // fetched, so a non-admin never gets an error toast and an empty page.
  const { capabilities, hasLoaded: capabilitiesLoaded } = useCapabilities();
  const isAdmin = Boolean(capabilities?.isAdmin);

  const [queue, setQueue] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  const setParam = useCallback((changes) => {
    const next = new URLSearchParams(location.search);
    for (const [k, v] of Object.entries(changes)) (v === null || v === undefined ? next.delete(k) : next.set(k, v));
    history.replace({ pathname: location.pathname, search: next.toString() ? `?${next}` : '' });
  }, [history, location.pathname, location.search]);

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const [q, f] = await Promise.all([fetchQueue(companyId), fetchFiles(companyId)]);
      setQueue(q);
      setFiles(f);
    } catch (err) {
      toast.error(apiError(err, 'Kunde inte hämta betalningar.').message);
    } finally {
      setLoading(false);
    }
  }, [companyId, isAdmin]);

  useEffect(() => { load(); }, [load]);

  // ?select=sala preselects every valid salary row (link from the payroll step).
  const initialSelection = useMemo(() => {
    if (params.get('select') !== 'sala' || !queue) return [];
    return queue.items.filter((i) => i.category_purpose === 'SALA' && i.valid).map((i) => i.instruction_id);
  }, [params, queue]);

  const queueCount = queue?.items?.length || 0;
  const openFiles = files.filter((f) => f.status === 'created').length;

  if (capabilitiesLoaded && !isAdmin) {
    return (
      <div className="col-12">
        <PageHeader icon={FileCheck2} title="Betalningar" subtitle="Betalfiler (ISO 20022) för löner och leverantörsbetalningar." />
        <EmptyState icon={Lock} title="Endast för administratörer" description="Betalfiler skapas och hanteras av företagets administratörer." />
      </div>
    );
  }

  return (
    <div className="col-12">
      <PageHeader
        icon={FileCheck2}
        title="Betalningar"
        subtitle="Betalfiler (ISO 20022) för löner och leverantörsbetalningar. Filen laddas upp och signeras i internetbanken."
        actions={(
          <button type="button" className="ll-btn ll-btn--secondary ll-btn--sm" onClick={load} disabled={loading}>
            <RefreshCw size={14} /> Uppdatera
          </button>
        )}
      />

      <div className="sub-steps" style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button type="button" className={`ll-btn ll-btn--sm ${tab === 'queue' ? 'll-btn--primary' : 'll-btn--secondary'}`} onClick={() => setParam({ tab: 'queue' })}>
          Betalningskö{queueCount > 0 ? ` (${queueCount})` : ''}
        </button>
        <button type="button" className={`ll-btn ll-btn--sm ${tab === 'files' ? 'll-btn--primary' : 'll-btn--secondary'}`} onClick={() => setParam({ tab: 'files' })}>
          Betalfiler{openFiles > 0 ? ` (${openFiles} ej uppladdad${openFiles === 1 ? '' : 'e'})` : ''}
        </button>
      </div>

      {tab === 'queue' ? (
        queue && (
          <PaymentQueueTab
            key={initialSelection.join(',')}
            companyId={companyId}
            queue={queue}
            loading={loading}
            onRefresh={load}
            initialSelection={initialSelection}
            onFileCreated={(file) => { load(); setParam({ tab: 'files', file: file.batch_id, select: null }); }}
          />
        )
      ) : (
        <PaymentFilesTab
          companyId={companyId}
          files={files}
          loading={loading}
          onRefresh={load}
          selectedId={selectedFile}
          onSelect={(id) => setParam({ file: id })}
        />
      )}
    </div>
  );
};

export default PaymentsPage;
